<template>
  <div class="app-shell home-page tool-page">
    <div class="app-shell__main">
      <AppHeader />

      <main class="home-main tool-main">
        <section class="hero-section tool-hero-section tool-page-hero">
          <div class="tool-hero-grid">
            <div class="tool-hero-copy">
              <span class="tool-eyebrow">AI Retouch</span>
              <h1 class="hero-title">图片去水印</h1>
              <p class="hero-description">轻松去掉图片中的水印、文字和杂物，让画面更干净自然。</p>
              <div class="tool-copy-pills">
                <span>手动涂抹与智能修复都可用</span>
                <span>处理完成后直接保存结果</span>
                <span>适合截图、海报和商品图</span>
              </div>
              <div class="tool-hero-summary">
                <article class="tool-hero-stat">
                  <strong>拖入即处理</strong>
                  <span>支持直接拖拽图片进入工作台，快速开始修复。</span>
                </article>
                <article class="tool-hero-stat">
                  <strong>手动与智能结合</strong>
                  <span>既能手动标记问题区域，也能交给 AI 自动补全背景。</span>
                </article>
                <article class="tool-hero-stat">
                  <strong>适合日常修图</strong>
                  <span>适合截图、商品图、海报物料和社媒配图快速清理。</span>
                </article>
              </div>
            </div>

            <div class="tool-workbench tool-workbench--link">
              <!-- <div class="tool-stage-note">
                <div class="tool-stage-note__title">图片修复工作台</div>
                <p>先上传图片，再标出需要去除的位置，最后由 AI 补齐背景和细节。</p>
              </div> -->
              <div class="image-editor-container">
                <div
                  v-if="!imageUrl"
                  class="upload-area"
                  :class="{ 'is-drag-active': isDragActive }"
                  @click="triggerUpload"
                  @dragenter.prevent="handleDragEnter"
                  @dragover.prevent="handleDragOver"
                  @dragleave.prevent="handleDragLeave"
                  @drop.prevent="handleDrop"
                >
                  <input type="file" ref="fileInput" accept="image/*" class="hidden-input" @change="handleFileChange" />
                  <div class="upload-placeholder">
                    <svg class="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="17 8 12 3 7 8"></polyline>
                      <line x1="12" y1="3" x2="12" y2="15"></line>
                    </svg>
                    <span class="upload-text">点击上传图片</span>
                    <span class="upload-hint">支持 JPG / PNG 格式，也可直接拖入图片</span>
                  </div>
                </div>

                <div v-else class="editor-area">
                  <div class="canvas-wrapper" ref="canvasWrapper" @contextmenu.prevent>
                    <img ref="imageRef" :src="imageUrl" class="editor-image" @load="initCanvas" crossorigin="anonymous" />
                    <canvas
                      ref="maskCanvas"
                      class="mask-canvas"
                      :style="maskCanvasStyle"
                      @mousedown="startDrawing"
                      @mousemove="draw"
                      @mouseup="stopDrawing"
                      @mouseleave="stopDrawing"
                      @touchstart="handleTouchStart"
                      @touchmove="handleTouchMove"
                      @touchend="stopDrawing"
                    ></canvas>
                  </div>

                  <div class="toolbar">
                    <div class="tool-group">
                      <button class="tool-btn" :class="{ active: mode === 'manual' }" @click="setMode('manual')">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
                          <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
                          <path d="M2 2l7.586 7.586"></path>
                          <circle cx="11" cy="11" r="2"></circle>
                        </svg>
                        <span>手动</span>
                      </button>
                      <button class="tool-btn" :class="{ active: mode === 'auto' }" @click="setMode('auto')">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z"></path>
                          <path d="M12 6v6l4 2"></path>
                        </svg>
                        <span>智能</span>
                      </button>
                    </div>

                    <div class="divider"></div>

                    <div class="tool-group">
                      <button class="tool-btn" @click="undo" :disabled="historyStep <= 0">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M9 14L4 9l5-5"></path>
                          <path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5v0a5.5 5.5 0 0 1-5.5 5.5H11"></path>
                        </svg>
                        <span>撤销</span>
                      </button>
                      <button class="tool-btn" @click="redo" :disabled="historyStep >= history.length - 1">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M15 14l5-5-5-5"></path>
                          <path d="M20 9H9.5A5.5 5.5 0 0 0 4 14.5v0A5.5 5.5 0 0 0 9.5 20H13"></path>
                        </svg>
                        <span>恢复</span>
                      </button>
                    </div>

                    <div class="divider"></div>

                    <div class="tool-group slider-group">
                      <span class="tool-label">笔触大小</span>
                      <input type="range" min="5" max="50" v-model.number="brushSize" class="brush-slider" />
                    </div>

                    <div class="tool-group action-group">
                      <button class="tool-btn secondary" @click="resetImage">重选图片</button>
                      <button class="tool-btn primary" @click="removeWatermark" :disabled="isProcessing">
                        <span v-if="isProcessing">处理中...</span>
                        <span v-else>消除水印</span>
                      </button>
                      <button class="tool-btn success" @click="saveImageResult" :disabled="!processedImage">保存结果</button>
                    </div>
                  </div>
                </div>

                <p class="hint-text">涂抹水印区域，点击“消除水印”即可去除</p>
              </div>
            </div>

            <div class="learn-more">
              <a href="#how-it-works" class="learn-more-link">
                <div class="learn-more-text">了解更多</div>
                <svg class="arrow-down" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <polyline points="6,9 12,15 18,9"></polyline>
                </svg>
              </a>
            </div>
          </div>
        </section>

        <section id="how-it-works" class="how-it-works-section tool-info-section">
          <div class="container">
            <div class="tool-section-head">
              <h2 class="section-title">如何使用</h2>
              <p>上传图片后标出需要处理的位置，剩下交给 AI 完成。</p>
            </div>
            <div class="steps-grid">
              <div class="step-card">
                <div class="step-number">1</div>
                <h3 class="step-title">上传图片</h3>
                <p class="step-description">选择需要处理的图片。</p>
              </div>
              <div class="step-card">
                <div class="step-number">2</div>
                <h3 class="step-title">涂抹水印</h3>
                <p class="step-description">框出要移除的文字或物体。</p>
              </div>
              <div class="step-card">
                <div class="step-number">3</div>
                <h3 class="step-title">一键消除</h3>
                <p class="step-description">AI 自动修复并生成结果图。</p>
              </div>
            </div>
          </div>
        </section>

        <section id="features" class="features-section tool-info-section">
          <div class="container">
            <div class="tool-section-head">
              <h2 class="section-title">功能特点</h2>
              <p>围绕图片修复场景，保留和首页一致的简洁表达与层级。</p>
            </div>
            <div class="features-grid">
              <div class="feature-card">
                <div class="feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z"></path>
                    <path d="M12 6v6l4 2"></path>
                  </svg>
                </div>
                <h3 class="feature-title">智能修复</h3>
                <p class="feature-description">自动补全背景与缺失细节。</p>
              </div>
              <div class="feature-card">
                <div class="feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21,15 16,10 5,21"></polyline>
                  </svg>
                </div>
                <h3 class="feature-title">高清画质</h3>
                <p class="feature-description">尽量保留原图清晰度。</p>
              </div>
              <div class="feature-card">
                <div class="feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12,6 12,12 16,14"></polyline>
                  </svg>
                </div>
                <h3 class="feature-title">简单易用</h3>
                <p class="feature-description">不需要专业修图经验。</p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>

    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import request from '@/utils/request'
import { getRemoveKey } from '@/utils/crypto'
import OSS from '@/utils/lib/aliyun-oss-sdk-6.17.1.min.js'
window.OSS = OSS;
import '@/utils/aliyun-upload-sdk-1.5.7.min.js'
import { showDialog, showConfirmDialog, showToast, showLoadingToast, closeToast } from 'vant'

export default {
  name: 'AiImageRemove',
  components: {
    AppHeader,
    AppFooter
  },
  data() {
    return {
      imageUrl: null, // Display URL (local blob or remote)
      remoteImageUrl: null, // URL for backend (from upload response)
      processedImage: null, // Result image URL

      // Canvas State
      ctx: null,
      isDrawing: false,
      brushSize: 20,

      // History for Undo/Redo
      history: [],
      historyStep: -1,
      imageHistory: [], // Stack of { url, remoteUrl, maskHistory, maskStep }
      isRestoring: false, // Flag for canvas restoration

      // Loading
      isUploading: false,
      isProcessing: false,
      isDragActive: false,
      dragDepth: 0,

      // Canvas Scale
      scale: 1,
      imageOffsetX: 0,
      imageOffsetY: 0,
      imageDisplayWidth: 0,
      imageDisplayHeight: 0,
      imageType: 'jpg', // Default image type

      // VOD Upload
      uploader: null,
      vodUrl: "https://vod-sz.adaiyun.net/",

      // Auto Mask Mode
      mode: 'manual', // 'manual' | 'auto'
      points: [],
      preStrokeState: null, // Snapshot before current stroke
      isGeneratingMask: false
    }
  },
  mounted() {
    this.initVod()
    window.addEventListener('resize', this.updateMaskBounds)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateMaskBounds)
  },
  computed: {
    maskCanvasStyle() {
      if (!this.imageDisplayWidth || !this.imageDisplayHeight) {
        return { display: 'none' }
      }
      return {
        left: `${this.imageOffsetX}px`,
        top: `${this.imageOffsetY}px`,
        width: `${this.imageDisplayWidth}px`,
        height: `${this.imageDisplayHeight}px`
      }
    }
  },
  methods: {
    setMode(newMode) {
      if (this.mode === newMode) return

      // Clear current mask when switching modes
      this.clearMask()
      this.mode = newMode

      if (newMode === 'auto') {
        const hasShownHint = localStorage.getItem('hasShownAutoMaskHint')
        if (!hasShownHint) {
          // alert('手指“轻轻滑动”水印区域，将自动生成涂抹痕迹')
          showConfirmDialog({ message: '手指“轻轻滑动”水印区域，将自动生成涂抹痕迹' })
          localStorage.setItem('hasShownAutoMaskHint', 'true')
        }
      }
    },

    clearMask() {
      if (!this.ctx || !this.$refs.maskCanvas) return
      this.ctx.clearRect(0, 0, this.$refs.maskCanvas.width, this.$refs.maskCanvas.height)
      this.history = []
      this.historyStep = -1
      // Also clear mask history in current image state
      if (this.imageHistory.length > 0) {
        const current = this.imageHistory[this.imageHistory.length - 1]
        current.maskHistory = []
        current.maskStep = -1
      }
      this.saveHistory() // Save the empty state
    },

    initVod() {
      const that = this
      // Ensure globals are available
      if (!window.AliyunUpload || !window.AliyunUpload.Vod) {
        console.error('AliyunUpload SDK not loaded')
        return
      }
      this.uploader = new window.AliyunUpload.Vod({
        userId: "1048463214720895",
        region: "cn-shenzhen",
        partSize: 1048576,
        parallel: 5,
        retryCount: 3,
        retryDuration: 2,
        onUploadstarted: async function (uploadInfo) {
          console.log('onUploadstarted', uploadInfo)
          let url = "/aliyun/vod/create/upload-image-auth"
          let data = {
            title: uploadInfo.file.name,
            imageType: 'default'
          }

          try {
            const res = await request(url, {
              method: 'POST',
              body: data
            })

            if (res) {
              const { uploadAuth, uploadAddress, videoId } = res
              that.uploader.setUploadAuthAndAddress(uploadInfo, uploadAuth, uploadAddress, videoId)
            } else {
              console.error('Failed to get upload auth', res)
              that.isUploading = false
            }
          } catch (err) {
            console.error('Error getting upload auth', err)
            that.isUploading = false
          }
        },
        onUploadSucceed: function (uploadInfo) {
          console.log('onUploadSucceed', uploadInfo)
          const imageUrl = that.vodUrl + uploadInfo.object
          that.remoteImageUrl = imageUrl
          that.isUploading = false
          console.log('Upload success, remote URL:', imageUrl)
        },
        onUploadFailed: function (uploadInfo, code, message) {
          console.error('onUploadFailed', uploadInfo, code, message)
          that.isUploading = false
          alert('图片上传失败: ' + message)
        },
        onUploadProgress: function (uploadInfo, totalSize, loadedPercent) {
          console.log('onUploadProgress', uploadInfo, totalSize, loadedPercent)
        },
        onUploadTokenExpired: function (uploadInfo) {
          console.log('onUploadTokenExpired', uploadInfo)
          // Handle expiration if needed
        }
      })
    },

    triggerUpload() {
      const token = localStorage.getItem('token')
      if (!token) {
        console.log('token失效或不可用')
        window.dispatchEvent(new CustomEvent('auth-login-required', { detail: { msg: '登录失效或不可用，请重新登录' } }))
        return
      }
      this.$refs.fileInput.click()
    },

    async handleFileChange(e) {
      const file = e.target.files[0]
      e.target.value = ''
      if (!file) return
      await this.processSelectedFile(file)
    },

    handleDragEnter() {
      this.dragDepth += 1
      this.isDragActive = true
    },

    handleDragOver(e) {
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy'
      }
      this.isDragActive = true
    },

    handleDragLeave() {
      this.dragDepth = Math.max(0, this.dragDepth - 1)
      if (this.dragDepth === 0) {
        this.isDragActive = false
      }
    },

    async handleDrop(e) {
      this.dragDepth = 0
      this.isDragActive = false
      const file = e.dataTransfer?.files?.[0]
      if (!file) return
      await this.processSelectedFile(file)
    },

    async processSelectedFile(file) {
      if (!file.type.startsWith('image/')) {
        showToast('请拖入图片文件')
        return
      }

      // Local preview
      this.imageUrl = URL.createObjectURL(file)

      // Get image type from filename
      const extension = file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase()
      this.imageType = extension || 'jpg'

      this.processedImage = null
      this.history = []
      this.historyStep = -1
      this.imageHistory = [{
        url: this.imageUrl,
        remoteUrl: null,
        maskHistory: [],
        maskStep: -1
      }]

      // Upload
      this.isUploading = true

      if (this.uploader) {
        this.uploader.addFile(file, null, null, null, '{"Vod":{}}')
        this.uploader.startUpload()
      } else {
        console.error('Uploader not initialized')
        this.isUploading = false
        alert('上传组件初始化失败，请刷新重试')
      }
    },

    initCanvas() {
      const img = this.$refs.imageRef
      const canvas = this.$refs.maskCanvas

      if (!img || !canvas) return

      // Set canvas size to natural image size
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight

      this.ctx = canvas.getContext('2d')
      this.ctx.lineCap = 'round'
      this.ctx.lineJoin = 'round'

      this.updateMaskBounds()

      // Restore last mask state
      if (this.historyStep >= 0 && this.history[this.historyStep]) {
        this.ctx.putImageData(this.history[this.historyStep], 0, 0)
      } else {
        // Save initial empty state
        this.saveHistory()
      }
    },

    updateMaskBounds() {
      const img = this.$refs.imageRef
      const wrapper = this.$refs.canvasWrapper

      if (!img || !wrapper) return

      const imgRect = img.getBoundingClientRect()
      const wrapperRect = wrapper.getBoundingClientRect()

      this.imageDisplayWidth = imgRect.width
      this.imageDisplayHeight = imgRect.height
      this.imageOffsetX = Math.max(0, imgRect.left - wrapperRect.left)
      this.imageOffsetY = Math.max(0, imgRect.top - wrapperRect.top)
    },

    getCoordinates(e) {
      const canvas = this.$refs.maskCanvas
      const rect = canvas.getBoundingClientRect()

      let clientX, clientY
      if (e.touches && e.touches.length > 0) {
        clientX = e.touches[0].clientX
        clientY = e.touches[0].clientY
      } else {
        clientX = e.clientX
        clientY = e.clientY
      }

      // Calculate scaling factor
      const scaleX = canvas.width / rect.width
      const scaleY = canvas.height / rect.height

      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY
      }
    },

    startDrawing(e) {
      if (this.isProcessing || this.isGeneratingMask) return

      this.isDrawing = true

      // Save state before this stroke (for auto mode rollback)
      if (this.mode === 'auto') {
        this.preStrokeState = this.ctx.getImageData(0, 0, this.$refs.maskCanvas.width, this.$refs.maskCanvas.height)
        this.points = []
      }

      const { x, y } = this.getCoordinates(e)

      if (this.mode === 'auto') {
        this.points.push({ x: Math.round(x), y: Math.round(y) })
      }

      this.ctx.beginPath()
      this.ctx.moveTo(x, y)
      this.ctx.lineWidth = this.brushSize
      this.ctx.strokeStyle = 'rgba(255, 215, 0, 0.5)'
    },

    draw(e) {
      if (!this.isDrawing) return

      const { x, y } = this.getCoordinates(e)

      if (this.mode === 'auto') {
        this.points.push({ x: Math.round(x), y: Math.round(y) })
      }

      this.ctx.lineTo(x, y)
      this.ctx.stroke()
    },

    async stopDrawing() {
      if (!this.isDrawing) return
      this.isDrawing = false
      this.ctx.closePath()

      if (this.mode === 'manual') {
        this.saveHistory()
      } else if (this.mode === 'auto') {
        // Rollback visual feedback lines
        if (this.preStrokeState) {
          this.ctx.putImageData(this.preStrokeState, 0, 0)
        }

        await this.generateClickMask()
      }
    },

    handleTouchStart(e) {
      e.preventDefault()
      this.startDrawing(e)
    },

    handleTouchMove(e) {
      e.preventDefault()
      this.draw(e)
    },

    saveHistory() {
      if (!this.ctx || !this.$refs.maskCanvas) return

      // If we are not at the end of history, discard future states
      if (this.historyStep < this.history.length - 1) {
        this.history = this.history.slice(0, this.historyStep + 1)
      }

      const imageData = this.ctx.getImageData(0, 0, this.$refs.maskCanvas.width, this.$refs.maskCanvas.height)
      this.history.push(imageData)
      this.historyStep++
    },

    undo() {
      if (this.historyStep > 0) {
        this.historyStep--
        this.ctx.putImageData(this.history[this.historyStep], 0, 0)
      }
    },

    redo() {
      if (this.historyStep < this.history.length - 1) {
        this.historyStep++
        this.ctx.putImageData(this.history[this.historyStep], 0, 0)
      }
    },

    resetImage() {
      this.imageUrl = null
      this.remoteImageUrl = null
      this.processedImage = null
      this.isDragActive = false
      this.dragDepth = 0
      this.imageOffsetX = 0
      this.imageOffsetY = 0
      this.imageDisplayWidth = 0
      this.imageDisplayHeight = 0
      this.history = []
      this.historyStep = -1
      this.imageHistory = []
    },

    // Auto Mask Methods
    generateEvenlySpacedPoints(points, step) {
      if (points.length < 2) return points

      const result = [points[0]]
      for (let i = 1; i < points.length; i++) {
        const p1 = points[i - 1]
        const p2 = points[i]
        const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y)

        if (dist >= step) {
          result.push(p2)
        }
      }
      return result
    },

    async generateClickMask() {
      if (this.points.length === 0) return

      if (!this.remoteImageUrl) {
        alert('图片尚未上传完成，请稍候')
        return
      }

      this.isGeneratingMask = true
      showLoadingToast({
        message: '智能生成中...',
        forbidClick: true,
        duration: 0
      })
      try {
        const keyPoints = this.generateEvenlySpacedPoints(this.points, 6)

        const res = await request('/remove-watermark/local-image/gen-clicks-mask', {
          method: 'POST',
          contentType: 'application/json',
          body: {
            imageUrl: this.remoteImageUrl,
            points: keyPoints
          }
        })

        if (res) {
          await this.drawMaskFromBase64(res)
        }

      } catch (error) {
        console.error('Auto mask generation failed', error)
      } finally {
        closeToast()
        this.isGeneratingMask = false
      }
    },

    drawMaskFromBase64(base64) {
      return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => {
          this.ctx.drawImage(img, 0, 0, this.$refs.maskCanvas.width, this.$refs.maskCanvas.height)
          this.saveHistory()
          resolve()
        }
        if (base64.startsWith('data:image')) {
          img.src = base64
        } else {
          img.src = 'data:image/png;base64,' + base64
        }
      })
    },

    async removeWatermark() {
      if (!this.imageUrl || this.isProcessing) return

      this.isProcessing = true

      try {
        // 1. Get Mask Base64
        const maskDataUrl = this.$refs.maskCanvas.toDataURL('image/png')

        // 2. Get Key
        const keyRes = await request('/remove-watermark/create-key', { method: 'POST' })

        if (!keyRes || !keyRes.data) throw new Error('Failed to get key')

        const key = getRemoveKey(keyRes.data, keyRes.timestamp)

        // 3. Call Removal API
        const removeRes = await request('/remove-watermark/local-image2', {
          method: 'POST',
          contentType: 'application/x-www-form-urlencoded',
          body: {
            imageUrl: this.remoteImageUrl || this.imageUrl,
            imageType: this.imageType,
            mask: maskDataUrl,
            key: key
          }
        })

        // Success
        const newImageUrl = removeRes
        if (!newImageUrl) {
          throw new Error('Result image URL is empty')
        }

        // Snapshot current state before updating
        if (this.imageHistory.length > 0) {
          const currentEntry = this.imageHistory[this.imageHistory.length - 1]
          currentEntry.maskHistory = [...this.history]
          currentEntry.maskStep = this.historyStep
          currentEntry.remoteUrl = this.remoteImageUrl
        }

        this.processedImage = newImageUrl
        this.remoteImageUrl = newImageUrl
        this.imageUrl = newImageUrl

        // Reset history for the new image
        this.history = []
        this.historyStep = -1
        this.ctx.clearRect(0, 0, this.$refs.maskCanvas.width, this.$refs.maskCanvas.height)

        // Push new state
        this.imageHistory.push({
          url: newImageUrl,
          remoteUrl: newImageUrl,
          maskHistory: [],
          maskStep: -1
        })

      } catch (error) {
        console.error('Remove watermark failed', error)
        // Handle specific error codes
        if (error.code == 4010) {
          showDialog({ message: '非会员无法保存，请升级会员' })
        } else if (error.code == 405) {
          showDialog({ message: '编辑次数已用完' })
        } else if (error.code == 406) {
          showDialog({ message: '今日次数已达上限' })
        } else {
          showDialog({ message: error.message || '去除水印失败' })
        }
      } finally {
        this.isProcessing = false
      }
    },

    async saveImageResult(forceUseStatus = 0) {
      const status = Number(forceUseStatus) || 0
      const keyRes = await request('/remove-watermark/create-key', { method: 'POST' })

      if (!keyRes || !keyRes.data) throw new Error('Failed to get key')

      const key = getRemoveKey(keyRes.data, keyRes.timestamp)

      // 4. Call Save API
      request('/remove-watermark/local-image/create-one2', {
        method: 'POST',
        contentType: 'application/x-www-form-urlencoded',
        body: {
          key: key,
          imageUrl: this.processedImage,
          forceUseStatus: status
        }
      }).then((createOneRes) => {
        console.log(createOneRes)
        showConfirmDialog({
          title: '保存图片',
          message: '是否直接下载？或拷贝链接到浏览器打开',
          confirmButtonText: '直接下载',
          cancelButtonText: '拷贝链接',
        }).then(() => {
          this.saveImage(createOneRes)
        })
          .catch((error) => {
            // 这里可能是用户点击"取消"或"关闭"，也可能是对话框出错
            if (error === 'cancel') {
              // 明确是用户点击了"拷贝链接"
              this.copyToClipboard(createOneRes)
            } else {
              // 处理其他错误
              console.log(error)
            }
          })
      }).catch((error) => {
        console.error('Save image failed', error)
        // throw new Error('保存图片失败')
        if (error.code == 4010) {
          showConfirmDialog({ message: '积分不足，成为会员后再尝试' })
          .then(() => {
            // 跳转到积分页面
            this.$router.push({ name: 'WebPay'})
          }).catch(() => {
            // 跳转到积分页面
            // this.$router.push({ name: 'WebPay' })
          })
        } else if (error.code == 405) {
          showConfirmDialog({ message: '已达会员每日次数使用上限，是否继续使用积分？' })
          .then(() => {
            this.saveImageResult(1)
          }).catch(() => {

          })
        } else if (error.code == 406) {
          showConfirmDialog({ message: '已达会员每日100次使用上限且无可用积分，是否需要获取更多积分？' })
          .then(() => {
            // 跳转到积分页面
            this.$router.push({ name: 'WebPay' })
          }).catch(() => {
            // 跳转到积分页面
            // this.$router.push({ name: 'WebPay' })
          })
        } else {
          showDialog({ message: error.message || '去除水印失败' })
        }
      })

    },
    async copyToClipboard(text, options = {}) {
      const {
        show = true,
        successMsg = '复制成功',
        errorMsg = '复制失败，请手动复制',
      } = options

      // 验证输入
      if (!text || typeof text !== 'string') {
        const error = new Error('无效的文本内容')
        if (show) showToast(errorMsg)
        throw error
      }

      try {
        // 方法1: 优先使用现代 Clipboard API
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text)
          if (show) showToast(successMsg)
          return
        }

      } catch (error) {
        console.error('复制失败:', error)
        if (show) showToast(errorMsg)
        throw error
      }
    },

    async saveImage(imageUrl) {
      if (!imageUrl) return

      try {
        const response = await fetch(imageUrl)
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        // Try to get extension from url or blob type
        const ext = blob.type.split('/')[1] || 'png'
        link.download = `removed_watermark_${new Date().getTime()}.${ext}`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Download failed, falling back to direct link', error)
        // Fallback
        const link = document.createElement('a')
        link.href = imageUrl
        link.download = 'removed_watermark.png'
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    }
  }
}
</script>

<style scoped>
.tool-page .home-main {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0;
}

.tool-page .hero-section.tool-page-hero {
  padding: 28px;
}

.tool-page .tool-workbench--link {
  padding: 0;
  background: transparent;
  border: 0;
  box-shadow: none;
}

.tool-page .container,
.image-editor-container {
  width: 100%;
  max-width: none;
  margin: 0;
}

.image-editor-container {
  padding: 28px;
  background: rgba(255, 255, 255, 0.76);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 24px;
  box-shadow: 0 18px 45px rgba(50, 71, 117, 0.08);
}

.hidden-input {
  display: none;
}

.upload-area {
  width: 100%;
  min-height: 320px;
  border: 2px dashed rgba(79, 94, 122, 0.18);
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: linear-gradient(180deg, rgba(247, 249, 252, 0.88), rgba(255, 255, 255, 0.92));
  transition: 0.2s ease;
}

.upload-area:hover {
  border-color: rgba(79, 70, 229, 0.32);
  background: rgba(79, 70, 229, 0.04);
}

.upload-area.is-drag-active {
  border-color: rgba(79, 70, 229, 0.48);
  background: rgba(79, 70, 229, 0.08);
  box-shadow: inset 0 0 0 1px rgba(79, 70, 229, 0.12);
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.upload-icon {
  width: 52px;
  height: 52px;
  color: var(--brand);
}

.upload-text {
  font-size: 18px;
  font-weight: 700;
  color: var(--ink);
}

.upload-hint {
  font-size: 14px;
  color: var(--muted);
}

.editor-area {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.canvas-wrapper {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #0f172a;
  border-radius: 24px;
  overflow: hidden;
}

.editor-image {
  display: block;
  max-width: 100%;
  max-height: 60vh;
  object-fit: contain;
  user-select: none;
}

.mask-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  cursor: crosshair;
  touch-action: none;
}

.toolbar {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  padding: 16px;
  background: rgba(247, 249, 252, 0.9);
  border: 1px solid rgba(223, 230, 242, 0.92);
  border-radius: 20px;
}

.tool-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.tool-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 10px 14px;
  border: 1px solid rgba(223, 230, 242, 0.96);
  background: rgba(255, 255, 255, 0.92);
  border-radius: 14px;
  cursor: pointer;
  font-weight: 600;
  color: var(--ink);
  transition: 0.2s ease;
}

.tool-btn svg {
  width: 18px;
  height: 18px;
}

.tool-btn:hover:not(:disabled),
.tool-btn.active {
  border-color: rgba(79, 70, 229, 0.28);
  color: var(--brand-deep);
  background: rgba(79, 70, 229, 0.06);
}

.tool-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.tool-btn.primary {
  color: #fff;
  border-color: transparent;
  background: linear-gradient(135deg, #5b6bff 0%, #2f80ed 100%);
}

.tool-btn.primary:hover:not(:disabled) {
  color: #fff;
}

.tool-btn.success {
  color: #fff;
  border-color: transparent;
  background: linear-gradient(135deg, #16a34a 0%, #10b981 100%);
}

.tool-btn.secondary {
  background: rgba(248, 250, 252, 0.95);
}

.divider {
  width: 1px;
  height: 28px;
  background: rgba(223, 230, 242, 0.96);
}

.tool-label {
  font-size: 14px;
  color: var(--muted);
}

.brush-slider {
  width: 120px;
}

.hint-text {
  margin: 16px 0 0;
  color: var(--muted);
  line-height: 1.7;
}

.tool-page .tool-info-section .container {
  width: 100%;
}

.tool-page .steps-grid,
.tool-page .features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  padding: 0;
}

.tool-page .step-card,
.tool-page .feature-card {
  padding: 24px;
  text-align: left;
  background: rgba(255, 255, 255, 0.76);
  border: 1px solid rgba(223, 230, 242, 0.92);
  border-radius: 24px;
  box-shadow: 0 14px 28px rgba(50, 71, 117, 0.06);
  transition: 0.2s ease;
}

.tool-page .step-card:hover,
.tool-page .feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 22px 34px rgba(50, 71, 117, 0.1);
}

.step-number {
  width: 48px;
  height: 48px;
  margin: 0 0 16px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  background: linear-gradient(135deg, #5b6bff 0%, #2f80ed 100%);
}

.feature-icon {
  width: 52px;
  height: 52px;
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  color: var(--brand);
  background: rgba(79, 70, 229, 0.1);
}

.feature-icon svg {
  width: 24px;
  height: 24px;
}

.step-title,
.feature-title {
  margin: 0 0 10px;
  font-size: 20px;
  font-weight: 700;
  color: var(--ink);
}

.step-description,
.feature-description {
  margin: 0;
  color: var(--muted);
  line-height: 1.8;
}

.tool-page .learn-more {
  display: flex;
  justify-content: center;
}

.tool-page .section-title {
  margin-bottom: 0;
}

@media (max-width: 900px) {
  .tool-page .hero-section.tool-page-hero {
    padding: 20px;
  }

  .image-editor-container {
    padding: 20px;
  }

  .toolbar {
    padding: 14px;
  }

  .divider {
    display: none;
  }
}
</style>
